/**
 * Exact public-object existence check for photo buckets.
 *
 * One HEAD request to the object's existing permanent public URL. Returns true
 * ONLY when the object is confirmed present with a non-zero Content-Length.
 * Any 404, timeout, network/CORS error, or missing/zero length returns false —
 * callers must treat false as "not confirmed", never as proof of absence.
 *
 * Shared by durable capture and the upload queue so both use the same bounded
 * check. No listing, no retries, no parallel requests, no bucket change.
 */
import { buildPhotoUrl } from "@/lib/photoProxy";

/** Bound for the exact public-object check. */
export const EXACT_PUBLIC_CHECK_TIMEOUT_MS = 8_000;

export async function headPublicPhotoObject(bucket: string, path: string): Promise<boolean> {
  if (typeof fetch !== "function") return false;
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), EXACT_PUBLIC_CHECK_TIMEOUT_MS);
  try {
    const res = await Promise.race([
      fetch(buildPhotoUrl(bucket, path), { method: "HEAD", cache: "no-store", signal: controller?.signal }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("exact check timeout")), EXACT_PUBLIC_CHECK_TIMEOUT_MS + 50),
      ),
    ]);
    if (!res || !res.ok) return false;
    const len = Number(res.headers?.get?.("content-length") ?? NaN);
    return Number.isFinite(len) && len > 0;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
